import type { Logger } from 'pino';

type Recipient = string | { email: string };

/** Pulls the bare address out of `Name <addr>` form, which SendGrid accepts wherever a string is. */
export function normalizeEmailAddress(raw: string): string {
  return (raw.match(/<([^>]+)>/)?.[1] ?? raw).trim().toLowerCase();
}

export function normalizeAddress(recipient: Recipient): string {
  return normalizeEmailAddress(typeof recipient === 'string' ? recipient : recipient.email);
}

export function asList<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Returns the bcc entries not already present in `to` or `cc` and not repeated within `bcc`
 * itself, or undefined when none remain. SendGrid rejects a personalization whose to/cc/bcc
 * share any address (case-insensitively), failing the entire send.
 */
export function dropBccCollisions<T extends Recipient>({
  to,
  cc,
  bcc,
}: {
  to?: Recipient | Recipient[];
  cc?: Recipient | Recipient[];
  bcc?: T | T[];
}): T[] | undefined {
  const taken = new Set([...asList(to), ...asList(cc)].map(normalizeAddress));
  const kept = asList(bcc).filter((recipient) => {
    const address = normalizeAddress(recipient);
    if (taken.has(address)) return false;
    taken.add(address);
    return true;
  });
  return kept.length > 0 ? kept : undefined;
}

/** `success` is false only when the send threw; `sent` is true only once SendGrid accepted the mail. */
export type SendEmailResult = { success: boolean; sent: boolean };

export const MAX_CC_RECIPIENTS = 10;

// What SendGrid accepts as a recipient (ASCII, no leading, trailing or doubled dot, no hyphen at a label edge, a real TLD).
export const EMAIL_SHAPE_REGEX =
  /^[A-Za-z0-9_%+-]+(\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export function isValidRecipientEmail(text: string): boolean {
  return EMAIL_SHAPE_REGEX.test(normalizeEmailAddress(text));
}

/** The server's cc rules in the shape `EmailRecipientsInput` from `@civilgrid/ui` takes as props. */
export const recipientAddressRules = {
  isValidAddress: isValidRecipientEmail,
  normalizeAddress: normalizeEmailAddress,
};

/**
 * Normalizes `raw` into a deduped cc list, silently dropping anything malformed or matching
 * `requesterEmail` (SendGrid rejects a personalization whose to/cc overlap) rather than
 * rejecting the whole export — a typo in a cc field must never fail someone's export.
 */
export function normalizeCcEmails(raw: unknown, requesterEmail: string): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  const requester = normalizeAddress(requesterEmail);
  const seen = new Set<string>();
  const kept: string[] = [];

  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const address = normalizeAddress(entry);
    if (!EMAIL_SHAPE_REGEX.test(address)) continue;
    if (address === requester) continue;
    if (seen.has(address)) continue;
    seen.add(address);
    kept.push(address);
    if (kept.length === MAX_CC_RECIPIENTS) break;
  }

  return kept.length > 0 ? kept : undefined;
}

const INTERNAL_EMAIL_DOMAIN = '@civilgrid.com';

/** Masks the local part so a dropped address is traceable to its domain without logging the PII. */
export function redactAddress(address: string): string {
  const at = address.lastIndexOf('@');
  return at === -1 ? '***' : `***${address.slice(at)}`;
}

type RecipientField = 'to' | 'cc' | 'bcc';

export type InternalRecipients<T extends Recipient> = {
  to: T[];
  cc: T[];
  bcc: T[];
  dropped: { field: RecipientField; address: string }[];
};

/** Splits `to`, `cc` and `bcc` into the CivilGrid addresses to keep and the external ones to drop. */
export function keepInternalRecipients<T extends Recipient>(data: {
  to?: T | T[];
  cc?: T | T[];
  bcc?: T | T[];
}): InternalRecipients<T> {
  const dropped: InternalRecipients<T>['dropped'] = [];

  const keep = (field: RecipientField, value: T | T[] | undefined) =>
    asList(value).filter((recipient) => {
      const address = normalizeAddress(recipient);
      if (address.endsWith(INTERNAL_EMAIL_DOMAIN)) return true;
      dropped.push({ field, address });
      return false;
    });

  return { to: keep('to', data.to), cc: keep('cc', data.cc), bcc: keep('bcc', data.bcc), dropped };
}

/**
 * Strips external addresses from `data`'s `to`, `cc` and `bcc` in place, returning false when the
 * send must be abandoned instead — no internal `to` survived, or the payload carries
 * `personalizations`, whose recipients SendGrid delivers to in place of the top-level fields.
 *
 * Call only outside production: staging, dev and the previews all really deliver mail.
 */
export function filterRecipientsForEnv<T extends Recipient>(
  data: { to?: T | T[]; cc?: T | T[]; bcc?: T | T[]; personalizations?: unknown[] },
  logger: Logger,
): boolean {
  if (data.personalizations && data.personalizations.length > 0) {
    logger.info('Skipped sending email because its personalizations carry recipients this filter cannot reach');
    return false;
  }

  const { to, cc, bcc, dropped } = keepInternalRecipients(data);
  for (const { field, address } of dropped) {
    logger.info(`Skipped sending email to ${redactAddress(address)} (${field}) because it is not a CivilGrid email`);
  }

  data.to = to;

  if (to.length === 0) {
    logger.info('Skipped sending email because all recipients were filtered out');
    delete data.cc;
    delete data.bcc;
    return false;
  }

  if (cc.length > 0) {
    data.cc = cc;
  } else {
    delete data.cc;
  }
  if (bcc.length > 0) {
    data.bcc = bcc;
  } else {
    delete data.bcc;
  }
  return true;
}
